import {
  invalidateSearchQueries,
  isCanonicalSearchQuery,
} from "@/lib/data/react-query/search/invalidation";
import {
  createUserV1,
  deleteUserV1,
  disableUserV1,
  enableUserV1,
  getCurrentUserV1,
  getAllDirectoryUsers,
  getSettingsUsers,
  getUserDirectory,
  type SettingsUsersParams,
  reassignUserAssignmentsV2,
  resetUserMfaV1,
  updateUserV1,
  type ReassignUserAssignmentsPayload,
} from "@/lib/data/requests";
import { generateS3ItemPreSignedUrlV1 } from "@/lib/data/requests/s3";
import { getSearchActor } from "@/lib/data/react-query/search/scope";
import { queryKeys } from "@/lib/data/static/query-keys";
import { type APIError, type APIResponse, type User } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import {
  infiniteQueryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";

export const getCurrentUserOptions = () => {
  return {
    queryKey: [queryKeys.currentUser, getSearchActor()],
    queryFn: async () => getCurrentUserV1(),
    refetchOnWindowFocus: false,
  };
};

export const getUsersOptions = (enabled: boolean = true) => {
  return {
    queryKey: [queryKeys.users, "directory", "all"],
    enabled,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      getAllDirectoryUsers(signal),
    staleTime: 60_000,
  };
};

export const getSettingsUsersOptions = (params: SettingsUsersParams) => ({
  queryKey: [queryKeys.users, "settings", params],
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    getSettingsUsers(params, signal),
});

export const getInfiniteUserDirectoryOptions = (
  search: string,
  enabled = true,
) =>
  infiniteQueryOptions({
    queryKey: [queryKeys.users, "directory", "infinite", search],
    enabled,
    initialPageParam: 0,
    queryFn: ({ pageParam, signal }) =>
      getUserDirectory(
        { limit: 50, offset: pageParam, search: search || undefined },
        signal,
      ),
    getNextPageParam: (page) => {
      const next = page.pagination.offset + page.data.length;
      return page.data.length > 0 && next < page.pagination.total
        ? next
        : undefined;
    },
    staleTime: 60_000,
  });

export const getDirectoryUsersByIdsOptions = (
  ids: string[],
  enabled = true,
) => ({
  queryKey: [queryKeys.users, "directory", "ids", ids],
  enabled: enabled && ids.length > 0,
  queryFn: async ({ signal }: { signal: AbortSignal }) => {
    const pages = [];
    for (let i = 0; i < ids.length; i += 100) {
      pages.push(
        await getUserDirectory(
          { ids: ids.slice(i, i + 100), limit: 100 },
          signal,
        ),
      );
    }
    return pages.flatMap((page) => page.data);
  },
  staleTime: 60_000,
});

export const useCreateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createUserV1>[0]) =>
      createUserV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error creating user");
    },
    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.users],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.permissionGroups],
      });
    },
    onSuccess: () => {
      toast.success(
        "User has been invited. They will receive an email shortly with a temporary password in their email.",
      );
    },
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateUserV1>[0]) =>
      updateUserV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error updating user");
    },

    onSuccess: (response) => {
      const updatedUser = response.data;
      queryClient.setQueryData(
        [queryKeys.currentUser, getSearchActor()],
        (oldData: APIResponse<User> | undefined) => {
          if (!oldData || oldData.data.id !== updatedUser.id) {
            return oldData;
          }

          return {
            ...oldData,
            data: {
              ...oldData.data,
              ...updatedUser,
            },
          };
        },
      );
      toast.success("User updated successfully");
    },
    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.users],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.permissionGroups],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.currentUser, getSearchActor()],
      });
    },
  });
};

export const useDisableUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => disableUserV1(id),
    onSuccess: () => {
      toast.success("User disabled successfully");
    },
    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.users],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.permissionGroups],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.currentUser, getSearchActor()],
      });
    },
  });
};

export const useEnableUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => enableUserV1(id),
    onSuccess: () => {
      toast.success("User enabled successfully");
    },
    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.users],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.permissionGroups],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.currentUser, getSearchActor()],
      });
    },
  });
};

export const useResetUserMfa = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => resetUserMfaV1(id),
    onSuccess: () => {
      toast.success("User MFA reset successfully");
    },
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error resetting user MFA");
    },
    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.users],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.permissionGroups],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.currentUser, getSearchActor()],
      });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteUserV1(id),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error deleting user");
    },
    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.users],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.permissionGroups],
      });
    },
    onSuccess: () => {
      toast.success("User deleted successfully");
    },
  });
};

export const useReassignUserAssignments = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ReassignUserAssignmentsPayload) =>
      reassignUserAssignmentsV2(data),
    onSuccess: () => {
      toast.success("User assignments reassigned successfully");
    },
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error reassigning user assignments");
    },
    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matterSummary],
      });
    },
  });
};

export const useUpdateUserProfilePicture = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { user: User; file: File }) => {
      const presignedURLRes = await generateS3ItemPreSignedUrlV1(
        "profile-picture",
      )({
        clientMethod: "put_object",
        fileName: data.file.name,
      });

      await fetch(presignedURLRes.data.presignedUrl, {
        method: "PUT",
        headers: {
          "Content-Type": data.file.type,
        },
        body: data.file,
      });

      return await updateUserV1({
        id: data.user.id,
        dateModified: data.user.dateModified,
        profilePicturePresignedUrlKey: presignedURLRes.data.key,
      });
    },
    onSuccess: () => {
      toast.success("Profile picture updated successfully");
    },
    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.users],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.permissionGroups],
      });
    },
  });
};

export const getUserProfilePictureOptions = (key?: string, id?: string) => {
  return {
    queryKey: [queryKeys.users, id, "photo"],
    queryFn: async () => {
      if (!key) {
        return null;
      }
      const presignedURLRes = await generateS3ItemPreSignedUrlV1(
        "profile-picture",
      )({
        clientMethod: "get_object",
        key,
      });
      const file = await fetch(presignedURLRes.data.presignedUrl, {
        method: "GET",
      });
      return file;
    },
  };
};
